import React, { FC, useCallback, useEffect, useMemo, useRef } from 'react';
import { loadBetradarWidgetsLib } from '../utils';
import _ from 'lodash';

declare type SrWidgetProps = {
    matchId: string;
    [prop: string]: unknown;
};

export interface SrWidgetOptions extends React.HTMLAttributes<HTMLDivElement> {
    widgetName: string;
    widgetProps: SrWidgetProps;
}

export const SrWidget: FC<SrWidgetOptions> = ({ widgetName, widgetProps, ...props }) => {
    const initPromise = useRef<DeferredPromise<void>>(new DeferredPromise());
    const widgetPropsMemoized = useDeepCompareMemoize<SrWidgetProps>(widgetProps);
    const logDebug = useWidgetDebug(widgetName, widgetPropsMemoized);
    const ref = useRefWithLifecycle(
        node => {
            const promise = initPromise.current;
            loadBetradarWidgetsLib(SIR =>
                SIR('addWidget', node, widgetName, widgetPropsMemoized, () => {
                    logDebug('addWidget', node);
                    promise.resolve(node);
                })
            );
        },
        node => {
            initPromise.current.then(() =>
                loadBetradarWidgetsLib(SIR =>
                    SIR('updateWidget', node, widgetPropsMemoized, () => {
                        logDebug('updateWidget', node);
                    })
                )
            );
        },
        node => {
            loadBetradarWidgetsLib(SIR => {
                SIR('removeWidget', node);
                logDebug('removeWidget', node);
            });
        },
        [widgetPropsMemoized]
    );

    return <div ref={ref} {...props} />;
};

function useRefWithLifecycle(
    onMount: (node: HTMLElement) => void,
    onUpdate: (node: HTMLElement) => void,
    onUnmount: (node: HTMLElement) => void,
    dependencies: unknown[] = []
) {
    const nodeRef = useRef<HTMLElement>(null);
    const dependenciesRef = useRef(null);

    const callbackRef = useCallback(node => {
        if (node) {
            nodeRef.current = node;
            onMount(node);
        } else {
            onUnmount(nodeRef.current);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!dependenciesRef.current) {
            dependenciesRef.current = dependencies;
            // do not call onUpdate on first render (onMount is called)
            return;
        }
        if (dependencies !== dependenciesRef.current) {
            onUpdate(nodeRef.current);
            dependenciesRef.current = dependencies;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, dependencies);
    return callbackRef;
}

const SR_WIDGET_DEBUG_LOG = localStorage.getItem('SR_WIDGET_DEBUG');
let counter = new Map<string, number>();
function useWidgetDebug(name: string, widgetProps: SrWidgetProps) {
    const widgetId = useMemo(() => {
        counter.set(name, (counter.get(name) || 0) + 1);
        return name + ' ' + counter.get(name);
    }, [name]);

    return (method: string, node: HTMLElement) => {
        if (SR_WIDGET_DEBUG_LOG) {
            console.log(`SR API call ${widgetProps.matchId} ${method} ${widgetId}`, {
                node,
                widgetProps
            });
        }
    };
}

function useDeepCompareMemoize<T>(value: T): T {
    const ref = useRef<T>();

    if (!_.isEqual(value, ref.current)) {
        ref.current = value;
    }

    return ref.current;
}

class DeferredPromise<T> implements Promise<T> {
    private readonly promise: Promise<T>;
    resolve!: Function;
    reject!: Function;
    then!: (val: any, fail?: any) => any;
    catch!: (val: any) => any;
    finally!: (val: any) => any;
    [Symbol.toStringTag]: string;

    constructor() {
        this.promise = new Promise((resolve, reject) => {
            this.resolve = resolve;
            this.reject = reject;
        });
        this.then = this.promise.then.bind(this.promise);
        this.catch = this.promise.catch.bind(this.promise);
        this.finally = this.promise.finally.bind(this.promise);
        this[Symbol.toStringTag] = 'Promise';
    }
}
